import { ErrorHandler, Injectable } from '@angular/core';

const RELOAD_FLAG_KEY = 'gp-chunk-reload-attempted';

/**
 * After a new deploy, a browser tab that already has the app open is still holding the
 * OLD main.js/runtime.js, which maps lazy-loaded route chunks to content hashes that no
 * longer match what the server now serves. Clicking into a lazy route then throws deep
 * inside webpack's own module loader ("Cannot read properties of undefined (reading
 * 'call')", or "Loading chunk X failed") — an uncaught promise rejection that leaves
 * Angular's zone in a broken state: the shell keeps rendering, but change detection has
 * effectively stopped, so the UI looks alive but nothing responds.
 *
 * There's no way to route around this from application code — the fix is a fresh page
 * load to pick up the new shell. This handler detects that specific failure signature and
 * does that reload once; a session flag stops it from looping if the server is genuinely
 * serving broken assets rather than just being mid-deploy.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  handleError(error: any): void {
    const message: string = error?.message || error?.rejection?.message || String(error ?? '');
    const isChunkLoadFailure =
      /Loading chunk [\w.-]+ failed/i.test(message) ||
      /ChunkLoadError/i.test(error?.name || error?.rejection?.name || '') ||
      /Cannot read propert(y|ies) of undefined \(reading 'call'\)/i.test(message);

    if (isChunkLoadFailure && !sessionStorage.getItem(RELOAD_FLAG_KEY)) {
      sessionStorage.setItem(RELOAD_FLAG_KEY, '1');
      console.error('Stale app bundle detected after a new deploy — reloading once to recover.', error);
      window.location.reload();
      return;
    }

    console.error(error);
  }
}

/** Call once the app has rendered successfully, so a later genuine chunk failure can still trigger a reload. */
export function clearChunkReloadFlag(): void {
  sessionStorage.removeItem(RELOAD_FLAG_KEY);
}
